import React, { useState, useEffect, useCallback } from 'react';
import {
  subscribeToCatalogueLeads,
  updateLeadStatus,
  updateLeadNotes,
  updateFollowUpDate,
  deleteLead,
  getLeadStats,
  LEAD_STATUSES,
  STATUS_LABELS,
  STATUS_COLORS
} from '../../firebase/catalogueLeadsService';
import CatalogueLeadsCSVImport from './CatalogueLeadsCSVImport';

const CatalogueLeadsManager = ({ dateRange }) => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedLead, setExpandedLead] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [leadsUnsubscribe, setLeadsUnsubscribe] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Handle date range changes
  useEffect(() => {
    if (leadsUnsubscribe) {
      leadsUnsubscribe();
    }

    const options = {};
    if (dateRange?.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange?.endDate) {
      options.endDate = dateRange.endDate;
    }

    console.log('📊 Setting up catalogue leads subscription with options:', options);

    const unsubscribe = subscribeToCatalogueLeads((leadsData) => {
      console.log('📥 Received catalogue leads:', leadsData.length);
      setLeads(leadsData);
      setFilteredLeads(leadsData);
      setLoading(false);
    }, options);

    setLeadsUnsubscribe(() => unsubscribe);

    // Get stats
    getLeadStats(options).then(setStats);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dateRange]);

  // Filter leads based on search and status
  useEffect(() => {
    let filtered = leads;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(lead =>
        lead.name.toLowerCase().includes(searchLower) ||
        lead.email.toLowerCase().includes(searchLower) ||
        lead.companyName.toLowerCase().includes(searchLower) ||
        lead.contactNumber.includes(searchTerm)
      );
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(lead => lead.status === selectedStatus);
    }

    setFilteredLeads(filtered);
  }, [leads, searchTerm, selectedStatus]);

  const handleStatusChange = async (leadId, newStatus) => {
    const success = await updateLeadStatus(leadId, newStatus);
    if (success) {
      // Refresh stats
      const options = {};
      if (dateRange?.startDate) options.startDate = dateRange.startDate;
      if (dateRange?.endDate) options.endDate = dateRange.endDate;
      getLeadStats(options).then(setStats);
    }
  };

  const handleSaveNotes = async (leadId) => {
    const success = await updateLeadNotes(leadId, notesText);
    if (success) {
      setEditingNotes(null);
      setNotesText('');
    }
  };

  const handleFollowUpDateChange = async (leadId, dateString) => {
    const date = dateString ? new Date(dateString) : null;
    await updateFollowUpDate(leadId, date);
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this catalogue lead?')) {
      const success = await deleteLead(leadId);
      if (success) {
        // Refresh stats
        const options = {};
        if (dateRange?.startDate) options.startDate = dateRange.startDate;
        if (dateRange?.endDate) options.endDate = dateRange.endDate;
        getLeadStats(options).then(setStats);
      }
    }
  };

  const toggleLeadExpansion = (leadId) => {
    setExpandedLead(expandedLead === leadId ? null : leadId);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') return date;
    return date.toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading catalogue leads...</div>;
  }

  return (
    <div className="catalogue-leads-manager">
      <div className="section-header-redesigned">
        <div className="header-top">
          <h2>Catalogue Download Leads</h2>
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📥 Import from CSV
          </button>
        </div>

        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-number">{stats.total || 0}</span>
            <span className="stat-label">Total Leads</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{ color: STATUS_COLORS.new }}>{stats.new || 0}</span>
            <span className="stat-label">New</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{ color: STATUS_COLORS.contacted }}>{stats.contacted || 0}</span>
            <span className="stat-label">Contacted</span>
          </div>
        </div>
      </div>

      {/* Mobile Stats */}
      <div className="quote-stats-mobile">
        <div className="quote-stat-mobile">
          <span className="quote-stat-number">{stats.total || 0}</span>
          <span className="quote-stat-label">Total</span>
        </div>
        <div className="quote-stat-mobile">
          <span className="quote-stat-number" style={{ color: STATUS_COLORS.new }}>{stats.new || 0}</span>
          <span className="quote-stat-label">New</span>
        </div>
        <div className="quote-stat-mobile">
          <span className="quote-stat-number" style={{ color: STATUS_COLORS.contacted }}>{stats.contacted || 0}</span>
          <span className="quote-stat-label">Contacted</span>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search by name, email, company, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="status-filter">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="status-select"
          >
            <option value="all">All Status</option>
            <option value={LEAD_STATUSES.NEW}>New</option>
            <option value={LEAD_STATUSES.CONTACTED}>Contacted</option>
          </select>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="no-data">
          <p>
            No catalogue leads found.{' '}
            {searchTerm || selectedStatus !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Leads will appear here when customers download the catalogue.'}
          </p>
        </div>
      ) : (
        <div className="quotes-list">
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="quote-card">
              <div className="quote-header" onClick={() => toggleLeadExpansion(lead.id)}>
                <div className="quote-basic-info">
                  <h3>{lead.name}</h3>
                  <p className="quote-company">{lead.companyName}</p>
                  <div className="quote-meta">
                    <span className="quote-date">{formatDate(lead.createdAt)}</span>
                    <span
                      className="quote-status"
                      style={{
                        backgroundColor: STATUS_COLORS[lead.status],
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    >
                      {STATUS_LABELS[lead.status]}
                    </span>
                  </div>
                </div>
                <div className="quote-summary">
                  <p><strong>Email:</strong> {lead.email}</p>
                  <p><strong>Phone:</strong> {lead.contactNumber}</p>
                  {lead.source === 'import' && (
                    <p style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      <em>Imported from historical data</em>
                    </p>
                  )}
                </div>
                <div className="quote-actions-header">
                  <div className="expand-icon">
                    {expandedLead === lead.id ? '▼' : '▶'}
                  </div>
                </div>
              </div>

              {expandedLead === lead.id && (
                <div className="quote-details">
                  <div className="quote-full-info">
                    <div className="info-section">
                      <h4>Contact Information</h4>
                      <p><strong>Name:</strong> {lead.name}</p>
                      <p><strong>Email:</strong> {lead.email}</p>
                      <p><strong>Phone:</strong> {lead.contactNumber}</p>
                      <p><strong>Company:</strong> {lead.companyName}</p>
                    </div>

                    <div className="info-section">
                      <h4>Lead Details</h4>
                      <p><strong>Download Date:</strong> {lead.dateOfEnquiry}</p>
                      <p><strong>Source:</strong> {lead.source === 'import' ? 'Historical Import' : 'Website'}</p>
                      <p><strong>Email Sent:</strong> {lead.emailSent ? 'Yes' : 'No'}</p>
                      {lead.contactedDate && (
                        <p><strong>Contacted On:</strong> {formatDate(lead.contactedDate)}</p>
                      )}
                    </div>

                    <div className="info-section">
                      <h4>Follow-up</h4>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                          Follow-up Date:
                        </label>
                        <input
                          type="date"
                          value={lead.followUpDate ? lead.followUpDate.toISOString().split('T')[0] : ''}
                          onChange={(e) => handleFollowUpDateChange(lead.id, e.target.value)}
                          style={{
                            padding: '8px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            width: '200px'
                          }}
                        />
                        {lead.followUpDate && (
                          <button
                            onClick={() => handleFollowUpDateChange(lead.id, null)}
                            style={{
                              marginLeft: '8px',
                              padding: '8px 12px',
                              backgroundColor: '#ff4d4f',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="info-section">
                      <h4>Notes</h4>
                      {editingNotes === lead.id ? (
                        <div>
                          <textarea
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px',
                              border: '1px solid #d9d9d9',
                              borderRadius: '4px',
                              marginBottom: '8px'
                            }}
                            placeholder="Add notes about this lead..."
                          />
                          <button
                            onClick={() => handleSaveNotes(lead.id)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#52c41a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '8px'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingNotes(null);
                              setNotesText('');
                            }}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#d9d9d9',
                              color: '#000',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p className="customer-message">
                            {lead.notes || <em style={{ color: '#8c8c8c' }}>No notes yet</em>}
                          </p>
                          <button
                            onClick={() => {
                              setEditingNotes(lead.id);
                              setNotesText(lead.notes || '');
                            }}
                            style={{
                              marginTop: '8px',
                              padding: '6px 12px',
                              backgroundColor: '#1890ff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {lead.notes ? 'Edit Notes' : 'Add Notes'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="info-section">
                      <h4>System Information</h4>
                      <p><strong>Lead ID:</strong> {lead.id}</p>
                      <p><strong>Created:</strong> {formatDate(lead.createdAt)}</p>
                    </div>
                  </div>

                  <div className="quote-actions">
                    <div className="status-update">
                      <label>Update Status:</label>
                      <select
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                        className="status-select-action"
                      >
                        <option value={LEAD_STATUSES.NEW}>New</option>
                        <option value={LEAD_STATUSES.CONTACTED}>Contacted</option>
                      </select>
                    </div>

                    <button
                      onClick={() => handleDeleteLead(lead.id)}
                      className="delete-btn"
                    >
                      Delete Lead
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="system-info" style={{ marginTop: '20px' }}>
        <div className="info-card">
          <h3>📧 Email Integration Active</h3>
          <p>
            All catalogue download requests are automatically tracked in the database and email notifications
            are sent to <strong>contact@beemascaffolding.sg</strong>. Use this dashboard to manage leads,
            track follow-ups, and update contact status.
          </p>
        </div>
      </div>

      {/* CSV Import Modal */}
      {showImportModal && (
        <CatalogueLeadsCSVImport
          onClose={() => setShowImportModal(false)}
          onSuccess={(results) => {
            setShowImportModal(false);
            alert(`Successfully imported ${results.success} catalogue leads!`);
            // Refresh stats
            const options = {};
            if (dateRange?.startDate) options.startDate = dateRange.startDate;
            if (dateRange?.endDate) options.endDate = dateRange.endDate;
            getLeadStats(options).then(setStats);
          }}
        />
      )}
    </div>
  );
};

export default CatalogueLeadsManager;
